<script setup lang="ts">
import { computed } from 'vue'
import { ActionsGroup, ActionItem, ActionsMenu, VButton } from '@/modules/shared/components'
import CallDetailsWrapper from '@/modules/investing/components/entities/distributions/callDetailsWrapper.vue'
import DistributionDetailsWrapper from '@/modules/investing/components/entities/distributions/distributionDetailsWrapper.vue'
import PortfolioDistributionDetailsWrapper from '@/modules/investing/components/entities/distributions/portfolioDistributionDetailsWrapper.vue'
import { hideActionsForInvestor } from '../utils/transaction-row'

// Action descriptors are shaped by use-transaction-actions; this component
// dispatches on `action.kind` for modal wrappers and falls through to ActionItem
// for the common link / href / onClick cases.
//
// Investor view:
//   - Fee rows render nothing (the edit page doesn't handle fees — #epics/94/2134).
//   - Non-fee rows render a single "View Details" button derived from the first
//     visible action in the resolved list (conventionally the "View" action).
// Admin view: unchanged — the existing 3-dot ActionsMenu.
const props = defineProps<{
  actions: any[]
  item: any
  group?: any
  isInvestor?: boolean
}>()

const visibleActions = computed(() =>
  props.actions.filter((a) => !a.visible || a.visible({ item: props.item, group: props.group })),
)

const primaryAction = computed(() => visibleActions.value[0] ?? null)

const hideEntirely = computed(
  () => Boolean(props.isInvestor) && (hideActionsForInvestor(props.item) || !primaryAction.value),
)

const showAsInvestorButton = computed(
  () => Boolean(props.isInvestor) && !hideActionsForInvestor(props.item) && !!primaryAction.value,
)
</script>

<template>
  <!-- Investor + fee (or no visible actions): render nothing. -->
  <template v-if="hideEntirely" />

  <!-- Investor + non-fee: a single "View Details" button mirroring the
       first visible action's target (modal wrapper, route, href, or click). -->
  <template v-else-if="showAsInvestorButton">
    <CallDetailsWrapper v-if="primaryAction.kind === 'call_modal'" v-bind="primaryAction.props({ item, group })">
      <template #default="{ fetchAndOpenModal }">
        <VButton variant="secondary" size="sm" @click="fetchAndOpenModal()">View Details</VButton>
      </template>
    </CallDetailsWrapper>

    <DistributionDetailsWrapper
      v-else-if="primaryAction.kind === 'distribution_modal'"
      v-bind="primaryAction.props({ item, group })"
    >
      <template #default="{ fetchAndOpenModal }">
        <VButton variant="secondary" size="sm" @click="fetchAndOpenModal">View Details</VButton>
      </template>
    </DistributionDetailsWrapper>

    <PortfolioDistributionDetailsWrapper
      v-else-if="primaryAction.kind === 'pd_modal'"
      v-bind="primaryAction.props({ item, group })"
    >
      <template #default="{ fetchAndOpenModal }">
        <VButton variant="secondary" size="sm" @click="fetchAndOpenModal()">View Details</VButton>
      </template>
    </PortfolioDistributionDetailsWrapper>

    <RouterLink
      v-else-if="primaryAction.tag === 'RouterLink' && primaryAction.to"
      :to="primaryAction.to({ item, group })"
    >
      <VButton variant="secondary" size="sm">View Details</VButton>
    </RouterLink>

    <a v-else-if="primaryAction.tag === 'a' && primaryAction.href" :href="primaryAction.href({ item, group })">
      <VButton variant="secondary" size="sm">View Details</VButton>
    </a>

    <VButton
      v-else
      variant="secondary"
      size="sm"
      @click="primaryAction.onClick ? primaryAction.onClick({ item, group }) : undefined"
    >
      View Details
    </VButton>
  </template>

  <!-- Admin: the existing 3-dot ActionsMenu, unchanged. -->
  <ActionsMenu v-else>
    <ActionsGroup>
      <template v-for="(action, idx) in actions" :key="idx">
        <template v-if="!action.visible || action.visible({ item, group })">
          <CallDetailsWrapper v-if="action.kind === 'call_modal'" v-bind="action.props({ item, group })">
            <template #default="{ fetchAndOpenModal }">
              <ActionItem text="View" @click="fetchAndOpenModal()" />
            </template>
          </CallDetailsWrapper>
          <DistributionDetailsWrapper
            v-else-if="action.kind === 'distribution_modal'"
            v-bind="action.props({ item, group })"
          >
            <template #default="{ fetchAndOpenModal }">
              <ActionItem text="View" @click="fetchAndOpenModal" />
            </template>
          </DistributionDetailsWrapper>
          <PortfolioDistributionDetailsWrapper
            v-else-if="action.kind === 'pd_modal'"
            v-bind="action.props({ item, group })"
          >
            <template #default="{ fetchAndOpenModal }">
              <ActionItem text="View" @click="fetchAndOpenModal()" />
            </template>
          </PortfolioDistributionDetailsWrapper>
          <ActionItem
            v-else
            :text="action.text"
            :tag="action.tag"
            :to="action.to ? action.to({ item, group }) : undefined"
            :href="action.href ? action.href({ item, group }) : undefined"
            :loading="action.loading ? action.loading({ item, group }) : undefined"
            @click="action.onClick ? action.onClick({ item, group }) : undefined"
          />
        </template>
      </template>
    </ActionsGroup>
  </ActionsMenu>
</template>
